import React, { useState, useRef, useContext } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { usePostHog } from 'posthog-js/react';
import { captureEvent, captureError } from "../utils/posthogUtils";
import "./BulkUploadModal.css";
import "./ResponsiveModal.css";
import { NotificationsContext } from "../context/NotificationsContext";
import getApiUrl from "../utils/api";

const splitAssigneeInput = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => splitAssigneeInput(item)).filter(Boolean);
  }

  if (value === null || value === undefined) {
    return [];
  }

  if (typeof value !== "string") {
    const str = String(value).trim();
    return str ? [str] : [];
  }

  return value
    .split(/[,;\n]+/)
    .map((segment) => segment.trim())
    .filter(Boolean);
};

const normalizeHeaderKey = (header) => {
  if (header === null || header === undefined) {
    return "";
  }

  return header
    .toString()
    .toLowerCase()
    .replace(/\(.*?\)/g, " ")
    .replace(/[^a-z0-9@]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");
};

const formatDateObject = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return "";
  }
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const toDDMMYYYY = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (typeof value === "number") {
    const millis = (value - 25569) * 86400 * 1000;
    const excelDate = new Date(millis);
    return formatDateObject(excelDate) || String(value);
  }

  if (value instanceof Date) {
    return formatDateObject(value);
  }

  const str = value.toString().trim();
  if (!str) {
    return "";
  }

  const isoMatch = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch;
    return `${dd.padStart(2, "0")}-${mm.padStart(2, "0")}-${yyyy}`;
  }

  const dayFirstMatch = str.match(/^([0-3]?\d)[-\/.]([0-1]?\d)[-\/.](\d{2,4})$/);
  if (dayFirstMatch) {
    let [, dd, mm, yy] = dayFirstMatch;
    dd = dd.padStart(2, "0");
    mm = mm.padStart(2, "0");
    if (yy.length === 2) {
      const numericYear = parseInt(yy, 10);
      yy = numericYear >= 50 ? `19${yy}` : `20${yy}`;
    }
    return `${dd}-${mm}-${yy}`;
  }

  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    return formatDateObject(parsed);
  }

  return str;
};

const excelSerialToDDMMYYYY = (serial) => {
  if (typeof serial !== "number") {
    return toDDMMYYYY(serial);
  }
  const millis = (serial - 25569) * 86400 * 1000;
  const date = new Date(millis);
  return formatDateObject(date) || String(serial);
};

const BulkUploadModal = ({ open, onClose, onBulkUploadSuccess }) => {
  const { addNotification } = useContext(NotificationsContext);
  const posthog = usePostHog();
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [serverErrors, setServerErrors] = useState([]);
  const [errorFileData, setErrorFileData] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv",
    ];

    if (!validTypes.includes(selectedFile.type)) {
      setError("Please upload a valid Excel file (.xlsx, .xls) or CSV file");
      return;
    }

    setFile(selectedFile);
    setError("");
    setSuccess("");
    setPreviewData([]);

    // Read and preview the file
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Convert to our expected format
        const processedData = processExcelData(jsonData);
        setPreviewData(processedData);
      } catch (err) {
        setError("Error reading file. Please check the file format.");
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const processExcelData = (jsonData) => {
    if (jsonData.length < 2) {
      setError("File must contain at least a header row and one data row");
      return [];
    }

    const headers = jsonData[0];
    const dataRows = jsonData.slice(1);

    return dataRows.map((row, index) => {
      const task = {};
      headers.forEach((header, colIndex) => {
        const value = row[colIndex] || "";
        const headerKey = normalizeHeaderKey(header);

        switch (headerKey) {
          case "title":
          case "task_name":
          case "taskname":
            task.title = value;
            break;
          case "description":
          case "desc":
            task.description = value;
            break;
          case "assigned_email":
          case "assignee_email":
          case "email":
          case "assigned_to":
          case "assigned_to_emails":
          case "assigned_to_email":
          case "assigned_to_emails_comma_separated":
          case "assignedto":
          case "employee":
          case "employee_name":
            task.assigned_to_raw = value;
            task.assigned_to_list = splitAssigneeInput(value);
            break;
          case "due_date":
          case "target_date":
          case "targetdate":
          case "target_completion_date":
          case "target_date_dd_mm_yyyy":
          case "duedate":
            task.due_date =
              typeof value === "number"
                ? excelSerialToDDMMYYYY(value)
                : toDDMMYYYY(value);
            break;
          case "start_date":
          case "startdate":
          case "start_date_dd_mm_yyyy":
            task.start_date =
              typeof value === "number"
                ? excelSerialToDDMMYYYY(value)
                : toDDMMYYYY(value);
            break;
          case "task_type":
          case "task type":
          case "tasktype":
          case "type":
          case "priority":
            task.priority = value;
            break;
          case "project":
            task.project = value;
            break;
          case "department":
          case "department_id":
            task.department = value;
            break;
          case "status":
            task.status = value || "Pending";
            break;
          case "kpi":
            task.kpi = value;
            break;
          default:
            break;
        }
      });

      const assignedList = task.assigned_to_list || [];
      const assignedDisplay = assignedList.join(", ");

      return {
        ...task,
        assigned_to: assignedDisplay || task.assigned_to_raw || "",
        assigned_to_display: assignedDisplay || task.assigned_to_raw || "",
        start_date: task.start_date || "",
        rowIndex: index + 2, // Excel row number (1-based, +1 for header)
        isValid: validateTask({
          ...task,
          assigned_to_list: assignedList,
          due_date: task.due_date,
        }),
      };
    });
  };

  const validateTask = (task) => {
    return (
      Boolean(task.title) &&
      Array.isArray(task.assigned_to_list) &&
      task.assigned_to_list.length > 0 &&
      Boolean(task.due_date)
    );
  };

  const handleUpload = async () => {
    if (!file || previewData.length === 0) {
      setError("Please select a file and ensure it contains valid data");
      return;
    }

    const validTasks = previewData.filter((task) => task.isValid);
    if (validTasks.length === 0) {
      setError("No valid tasks found. Please check your data format.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("tasks", JSON.stringify(validTasks));

      const response = await fetch(getApiUrl("api/task/bulkUpload"), {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      let result;
      const responseContentType = response.headers.get("content-type") || "";

      if (responseContentType.includes("application/json")) {
        result = await response.json();
      } else {
        const text = await response.text();
        result = text ? { message: text } : {};
      }

      if (response.ok) {
        if (result.success && result.createdCount > 0) {
          setSuccess(
            `✅ ${result.message} ${result.errorCount > 0
              ? `⚠️ ${result.errorCount} error(s) occurred.`
              : ""
            }`
          );
          setUploadProgress(100);

          if (result.errors && result.errors.length > 0) {
            setServerErrors(result.errors);
          }
          if (result.errorFile) {
            setErrorFileData({ content: result.errorFile, fileName: result.errorFileName });
          }

          // Fire a single summary notification
          try {
            addNotification({
              type: "task_created",
              title: "Bulk upload complete",
              message: `${result.createdCount} task(s) created${result.errorCount > 0 ? `, ${result.errorCount} error(s)` : ""
                }.`,
              meta: { created: result.createdCount, errors: result.errorCount },
            });
          } catch { }

          // Track successful bulk upload
          captureEvent(posthog, 'Bulk Upload Completed', {
            taskCount: result.createdCount,
            errorCount: result.errorCount || 0,
            fileName: file.name
          });

          // Close modal after a delay if no errors
          if (!result.errors || result.errors.length === 0) {
            setTimeout(() => {
              if (onBulkUploadSuccess) onBulkUploadSuccess();
              handleClose();
            }, 3000);
          }
        } else {
          captureError(posthog, 'Bulk Upload Failed', new Error(result.message || "Upload failed"), { taskCount: validTasks.length });
          setError(`❌ ${result.message || "Upload failed"}`);
          if (result.errors) setServerErrors(result.errors);
          if (result.errorFile) setErrorFileData({ content: result.errorFile, fileName: result.errorFileName });
        }
      } else {
        captureError(posthog, 'Bulk Upload Failed', new Error(result.message || result.error || "Upload failed"), { taskCount: validTasks.length });
        setError(result.message || result.error || "Upload failed");
        if (result.errors) setServerErrors(result.errors);
        if (result.errorFile) setErrorFileData({ content: result.errorFile, fileName: result.errorFileName });
      }
    } catch (err) {
      const fallbackMessage =
        err?.message && !/NetworkError/i.test(err.message)
          ? err.message
          : "Network error. Please try again.";
      captureError(posthog, 'Bulk Upload Failed', err, { taskCount: validTasks?.length });
      setError(fallbackMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreviewData([]);
    setError("");
    setSuccess("");
    setServerErrors([]);
    setErrorFileData(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const downloadErrorFile = () => {
    if (!errorFileData || !errorFileData.content) return;

    try {
      const base64Content = errorFileData.content;
      const byteCharacters = atob(base64Content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = errorFileData.fileName || 'bulk_upload_errors.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading error file:", err);
      toast.error("Failed to download error file.");
    }
  };

  const downloadTemplate = () => {
    captureEvent(posthog, 'Bulk Upload Template Downloaded');
    const template = [
      [
        "Title",
        "Description",
        "Assigned_To_Emails (comma separated)",
        "Target_Date (DD-MM-YYYY)",
        "Start_Date (DD-MM-YYYY)",
        "Priority",
        "Project",
        "Department",
        "Status",
        "KPI",
      ],
      [
        "Sample Task 1",
        "This is a sample task description",
        "johndoe@example.com, anita.sharma@vmart.co.in",
        "15-01-2024",
        "10-01-2024",
        "High",
        "Project A",
        "IT",
        "Pending",
        "High",
      ],
      [
        "Sample Task 2",
        "Another sample task",
        "janesmith@example.com",
        "25/01/2024",
        "20/01/2024",
        "Medium",
        "Project B",
        "Design",
        "Planned",
        "Medium",
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tasks Template");

    XLSX.writeFile(wb, "task_bulk_upload_template.xlsx");
  };

  if (!open) return null;

  return (
    <div className="bulk-upload-overlay modal-overlay-responsive">
      <div className="bulk-upload-modal modal-content-responsive">
        <div className="bulk-upload-header modal-header-responsive">
          <h3 className="modal-title-responsive">Bulk Upload Tasks</h3>
          <button
            className="bulk-upload-close modal-close-responsive"
            onClick={handleClose}
          >
            ×
          </button>
        </div>

        <div className="bulk-upload-content modal-body-responsive modal-scrollable-content">
          <div className="upload-section">
            <div className="file-upload-area">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                className="file-input"
              />
              <div className="upload-placeholder">
                <div className="upload-icon">📁</div>
                <p>Click to upload Excel file or drag and drop</p>
                <p className="upload-hint">
                  Supports .xlsx, .xls, and .csv files
                </p>
              </div>
            </div>

            <div className="template-section">
              <button className="template-btn" onClick={downloadTemplate}>
                📥 Download Template
              </button>
              <p className="template-hint">
                Use our template to ensure correct format. Separate multiple
                assignee emails with commas and enter target/start dates as
                DD-MM-YYYY (or ISO YYYY-MM-DD). Leave start date blank if you
                only want a due date.
              </p>
            </div>
          </div>

          {error && <div className="bulk-upload-error">{error}</div>}
          {success && <div className="bulk-upload-success">{success}</div>}

          {serverErrors && serverErrors.length > 0 && (
            <div className="server-errors-section">
              <div className="server-errors-header">
                <h4>Submission Errors ({serverErrors.length})</h4>
                {errorFileData && (
                  <button className="download-error-file-btn" onClick={downloadErrorFile}>
                    📥 Download Marked Error File
                  </button>
                )}
              </div>
              <ul className="server-errors-list">
                {serverErrors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {previewData.length > 0 && (
            <div className="preview-section">
              <h4>Preview ({previewData.length} tasks)</h4>
              <div className="preview-table-container">
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Title</th>
                      <th>Assigned To (comma separated)</th>
                      <th>Start Date (DD-MM-YYYY)</th>
                      <th>Target Date (DD-MM-YYYY)</th>
                      <th>Status</th>
                      <th>Valid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((task, index) => (
                      <tr
                        key={index}
                        className={task.isValid ? "valid-row" : "invalid-row"}
                      >
                        <td>{task.rowIndex}</td>
                        <td>{task.title || "Missing"}</td>
                        <td>{task.assigned_to || "Missing"}</td>
                        <td>{task.start_date || "—"}</td>
                        <td>{task.due_date || "Missing"}</td>
                        <td>{task.status || "Pending"}</td>
                        <td>
                          <span
                            className={`validity-badge ${task.isValid ? "valid" : "invalid"
                              }`}
                          >
                            {task.isValid ? "✓" : "✗"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {loading && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p>Uploading tasks... {uploadProgress}%</p>
            </div>
          )}
        </div>

        <div className="bulk-upload-actions modal-footer-responsive">
          <button className="bulk-upload-cancel" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="bulk-upload-submit"
            onClick={handleUpload}
            disabled={loading || previewData.length === 0}
          >
            {loading ? "Uploading..." : "Upload Tasks"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
